import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { ThreadMessageView } from '@/lib/api/generated/model';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import { displayNameOf } from '@/shared/components/accountIdentity';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { absoluteDateTime, formatRelativeTime } from '@/shared/utils/time';
import { formatCost } from '../executionHistory';
import { DRAWER_PROSE_CLASS } from '../prose';
import { replyLine } from './replyState';
import { threadMessageElementId } from './threadLink';
import {
  useAddThreadMessageToTaskContext,
  useRetryThreadReply,
  type RunThreadAddress,
} from './useRunThread';
import { useCanWork } from '@/features/workspaces/canWork';

export interface ThreadMessageRowProps {
  message: ThreadMessageView;
  address: RunThreadAddress;
  filePaths: MarkdownFilePaths;
  /** Opens the task's Comments; absent where there is no drawer to switch. */
  onOpenComments?: () => void;
  /** The message a link landed on: ringed so the eye finds it. */
  focused?: boolean;
}

/**
 * One message on a run's thread (spec 149), drawn as a comment is: who wrote
 * it, when, and the text as markdown. What a comment does not have is said
 * underneath — a person's message, where its answer stands; the agent's, what
 * it cost and whether it was cut short — and every message can be copied into
 * the task's context.
 *
 * Nothing edits or deletes (Q13): a thread is a record of what was asked and
 * answered.
 */
export function ThreadMessageRow({
  message,
  address,
  filePaths,
  onOpenComments,
  focused = false,
}: ThreadMessageRowProps) {
  const moment = absoluteDateTime(message.created_at);
  const isAgent = message.author_kind === 'agent';
  const line = !isAgent && message.reply ? replyLine(message.reply) : null;

  return (
    <article
      id={threadMessageElementId(message.id)}
      data-testid="thread-message"
      // `scroll-mt`: a link lands on it below the run's sticky header, not under it.
      className={`border-border bg-card flex scroll-mt-16 flex-col gap-1.5 rounded-lg border p-3 ${
        focused ? 'ring-ring ring-2' : ''
      }`}
    >
      <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
        {isAgent ? (
          <span className="text-foreground font-medium">Agent</span>
        ) : message.author ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {/* Decorative: the name is right beside it. */}
            <UserAvatar
              id={message.author.id}
              displayName={message.author.display_name}
              email={message.author.email}
            />
            <span
              className="text-foreground truncate font-medium"
              title={message.author.email}
            >
              {displayNameOf(message.author.display_name, message.author.email)}
            </span>
          </span>
        ) : (
          <span className="text-foreground font-medium">Unknown person</span>
        )}
        {/* The comment row's moment: absolute on the line, relative on hover. */}
        <time
          className="shrink-0 tabular-nums"
          dateTime={moment.machine}
          title={
            moment.machine ? formatRelativeTime(message.created_at) : undefined
          }
        >
          {moment.text}
        </time>
      </div>

      <MarkdownLazy
        content={message.body}
        className={DRAWER_PROSE_CLASS}
        filePaths={filePaths}
      />

      <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {line ? (
          <ReplyStatus
            text={line.text}
            live={line.live}
            retryable={line.retryable}
            address={address}
            messageId={message.id}
          />
        ) : null}
        {isAgent && message.cost != null ? (
          <span className="tabular-nums" title="What this answer cost">
            {formatCost(message.cost)}
          </span>
        ) : null}
        {isAgent && message.cut_short ? (
          <span>Cut short: the agent reached its limit</span>
        ) : null}
        <span className="ms-auto">
          <AddToTaskContext
            message={message}
            address={address}
            onOpenComments={onOpenComments}
          />
        </span>
      </div>
    </article>
  );
}

/**
 * Where a person's answer stands. A live state is announced politely as it
 * changes; a failed or refused one says why and offers Retry beside it.
 */
function ReplyStatus({
  text,
  live,
  retryable,
  address,
  messageId,
}: {
  text: string;
  live: boolean;
  retryable: boolean;
  address: RunThreadAddress;
  messageId: string;
}) {
  const retry = useRetryThreadReply();
  const canWork = useCanWork();
  return (
    <span className="flex items-center gap-2">
      <span
        aria-live="polite"
        data-testid="reply-status"
        className={`flex items-center gap-1.5 ${retryable ? 'text-state-danger' : ''}`}
      >
        {live ? (
          // The word carries the meaning; the pulse stops for reduced motion.
          <span
            aria-hidden="true"
            className="bg-board-state-done size-1.5 rounded-full motion-safe:animate-pulse"
          />
        ) : null}
        {text}
      </span>
      {retryable && canWork ? (
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={retry.isPending}
          onClick={() =>
            retry.mutate(
              { ...address, messageId },
              { onError: (error) => toast.error(error.message) },
            )
          }
        >
          {retry.isPending ? 'Retrying…' : 'Retry'}
        </Button>
      ) : null}
    </span>
  );
}

/**
 * Copies the message into the task's Comments, which later runs read (FR-005).
 * Once done, it says so for good, and leads to the comment where it can.
 */
function AddToTaskContext({
  message,
  address,
  onOpenComments,
}: {
  message: ThreadMessageView;
  address: RunThreadAddress;
  onOpenComments?: () => void;
}) {
  const add = useAddThreadMessageToTaskContext();
  const canWork = useCanWork();
  if (message.task_comment_id || message.added_to_task_context_at) {
    return onOpenComments ? (
      <button
        type="button"
        onClick={onOpenComments}
        className="hover:text-foreground underline underline-offset-4"
      >
        Added to task context
      </button>
    ) : (
      <span>Added to task context</span>
    );
  }
  // Someone only reading the workspace adds nothing to it (spec 189).
  if (!canWork) return null;
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      disabled={add.isPending}
      onClick={() =>
        add.mutate(
          { ...address, messageId: message.id },
          { onError: (error) => toast.error(error.message) },
        )
      }
    >
      {add.isPending ? 'Adding…' : 'Add to task context'}
    </Button>
  );
}
